import { useReducer, useCallback, useRef, useEffect } from "react"
import { calculateMinWidth } from "src/utils/table-render-functions"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    ultimoNegocio: { data_referencia: "", pupar: "", spread_credito: "" },
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

const getCetipConsulta = (cetipIsin, dadosEmpresa) => {
    if (dadosEmpresa?.CETIP) return dadosEmpresa.CETIP
    if (dadosEmpresa?.cetip) return dadosEmpresa.cetip

    if (cetipIsin?.CETIP) return cetipIsin.CETIP
    if (cetipIsin?.cetip) return cetipIsin.cetip

    if (typeof cetipIsin === "string") return cetipIsin

    return ""
}

export const useUltimoNegocioTable = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)
    const cetipConsulta = getCetipConsulta(cetipIsin, dadosEmpresa)
    // garante que os dados do cetip anterior não preencham a tabela ao mudar o cetip
    const loadedRef = useRef(cetipConsulta)
    
    useEffect(() => {
        if (loadedRef.current === cetipConsulta) return

        loadedRef.current = cetipConsulta
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipConsulta])

    const buildTable = useCallback(() => {
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${dadosEmpresa?.CETIP || cetipConsulta || ""}`,
                        `ISIN: ${dadosEmpresa?.ISIN || ""}`,
                    ],
                },
            ],
        })
    }, [cetipConsulta, dadosEmpresa])

    const loadTableRows = useCallback(async () => {
        if (!cetipConsulta || cetipConsulta.length <= 4) return

        buildTable()
        dispatch({ type: "SET_LOADING", payload: true })

        try {
            const response = await emissaoService.ultimoNegocios(
                "get",
                `?CETIP=${cetipConsulta.toUpperCase()}`
            )
            const results = Array.isArray(response.body)
                ? response.body
                : response.body?.results || []

            if (!results || results.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                const rows = ordenaPorDataReferencia(results, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )
                dispatch({ type: "SET_ROWS", payload: rows })
            }
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Não há dados para o CETIP consultado.",
                    type: "warning",
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [emissaoService, cetipConsulta, buildTable])

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthUltimoNegocio: calculateMinWidth(
                state.rows,
                "SpreadCreditoFinal"
            ),
        },
        actions: {
            loadTableRows,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}